import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, ExternalLink, Download, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AgentPhoto from '@/components/AgentPhoto';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const emptyForm = {
  name: '',
  slug: '',
  photo_url: '',
  agency_id: '',
  review_url: '',
  gender: 'M',
  notes: '',
};

export default function AdminAgents() {
  useAuth(); // ensure authenticated
  const [agents, setAgents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importAgencyId, setImportAgencyId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importResultsOpen, setImportResultsOpen] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [agentsRes, agenciesRes] = await Promise.all([
        axios.get(`${API}/agents`),
        axios.get(`${API}/agencies`)
      ]);
      setAgents(agentsRes.data);
      setAgencies(agenciesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (agent = null) => {
    if (agent) {
      setSelectedAgent(agent);
      setFormData({
        name: agent.name,
        slug: agent.slug || '',
        photo_url: agent.photo_url || '',
        agency_id: agent.agency_id,
        review_url: agent.review_url,
        gender: agent.gender || 'M',
        notes: agent.notes || '',
      });
    } else {
      setSelectedAgent(null);
      setFormData(emptyForm);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.agency_id || !formData.review_url.trim()) {
      toast.error('Nome, agência e URL de reviews são obrigatórios');
      return;
    }

    setSubmitting(true);

    try {
      if (selectedAgent) {
        await axios.put(`${API}/agents/${selectedAgent.id}`, formData);
        toast.success('Consultor actualizado com sucesso');
      } else {
        await axios.post(`${API}/agents`, formData);
        toast.success('Consultor criado com sucesso');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving agent:', error);
      const message = error.response?.data?.detail || 'Erro ao guardar consultor';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (agent) => {
    try {
      await axios.put(`${API}/agents/${agent.id}`, { active: !agent.active });
      toast.success(`Consultor ${!agent.active ? 'activado' : 'desactivado'}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling agent:', error);
      toast.error('Erro ao alterar estado do consultor');
    }
  };

  const handleDelete = async () => {
    if (!selectedAgent) return;

    try {
      await axios.delete(`${API}/agents/${selectedAgent.id}`);
      toast.success('Consultor eliminado com sucesso');
      setDeleteDialogOpen(false);
      setSelectedAgent(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Erro ao eliminar consultor');
    }
  };

  const handleImport = async () => {
    if (!importAgencyId) {
      toast.error('Seleccione uma agência');
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(`${API}/agents/import`, {
        agency_id: importAgencyId,
      });
      setImportDialogOpen(false);
      setImportAgencyId('');
      setImportResults(response.data);
      setImportResultsOpen(true);
      fetchData();
    } catch (error) {
      console.error('Error importing agents:', error);
      const message = error.response?.data?.detail || 'Erro ao importar consultores';
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#B5985A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-agents">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212121]" style={{ fontFamily: 'Montserrat' }}>
            Consultores
          </h1>
          <p className="text-gray-500 mt-1">Gerir consultores e respectivos perfis de review</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setImportDialogOpen(true)}
            variant="outline"
            className="btn-outline flex items-center gap-2"
            disabled={agencies.filter(a => a.active).length === 0}
            data-testid="import-agents-btn"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Importar</span>
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="btn-gold flex items-center gap-2"
            disabled={agencies.filter(a => a.active).length === 0}
            data-testid="add-agent-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Novo Consultor</span>
          </Button>
        </div>
      </div>

      {agencies.filter(a => a.active).length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 mb-6 text-yellow-800">
          Precisa de ter pelo menos uma agência activa para adicionar consultores.
        </div>
      )}

      {agents.length === 0 ? (
        <div className="bg-white border border-gray-100 p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhum consultor</h3>
          <p className="text-gray-400 mb-4">Comece por adicionar o primeiro consultor</p>
          <Button
            onClick={() => handleOpenDialog()}
            className="btn-gold"
            disabled={agencies.filter(a => a.active).length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Consultor
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Consultor</th>
                  <th>Sexo</th>
                  <th>Agência</th>
                  <th>URL Reviews</th>
                  <th>Estado</th>
                  <th className="text-right">Acções</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => (
                  <tr key={agent.id} data-testid={`agent-row-${agent.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <AgentPhoto src={agent.photo_url} alt={agent.name} size="sm" />
                        <span className="font-medium text-[#212121]">{agent.name}</span>
                      </div>
                    </td>
                    <td className="text-gray-500">
                      {agent.gender === 'F' ? 'Feminino' : 'Masculino'}
                    </td>
                    <td className="text-gray-500">{agent.agency_name}</td>
                    <td>
                      <a
                        href={agent.review_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#B5985A] hover:text-[#A3884A] flex items-center gap-1 text-sm"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Ver perfil
                      </a>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={agent.active}
                          onCheckedChange={() => handleToggleActive(agent)}
                          data-testid={`toggle-agent-${agent.id}`}
                        />
                        <span className={agent.active ? 'badge-active' : 'badge-inactive'}>
                          {agent.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(agent)}
                          className="hover:bg-[#B5985A]/10 hover:text-[#B5985A]"
                          data-testid={`edit-agent-${agent.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedAgent(agent);
                            setDeleteDialogOpen(true);
                          }}
                          className="hover:bg-red-50 hover:text-red-500"
                          data-testid={`delete-agent-${agent.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="agent-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>
              {selectedAgent ? 'Editar Consultor' : 'Novo Consultor'}
            </DialogTitle>
            <p className="text-sm text-gray-500">
              {selectedAgent ? 'Actualize os dados do consultor' : 'Preencha os dados para criar um novo consultor'}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-uppercase">Nome do Consultor *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: João Silva"
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                data-testid="agent-name-input"
              />
            </div>
            <div>
              <label className="label-uppercase">Slug (nome curto para URL)</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="Ex: joao-silva"
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                data-testid="agent-slug-input"
              />
            </div>
            <div>
              <label className="label-uppercase">Sexo *</label>
              <Select
                value={formData.gender}
                onValueChange={(value) => setFormData({ ...formData, gender: value })}
              >
                <SelectTrigger
                  className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                  data-testid="agent-gender-select"
                >
                  <SelectValue placeholder="Seleccione o sexo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculino</SelectItem>
                  <SelectItem value="F">Feminino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-uppercase">URL da Foto</label>
              <Input
                value={formData.photo_url}
                onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                placeholder="https://..."
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                data-testid="agent-photo-input"
              />
            </div>
            <div>
              <label className="label-uppercase">Agência *</label>
              <Select
                value={formData.agency_id}
                onValueChange={(value) => setFormData({ ...formData, agency_id: value })}
              >
                <SelectTrigger
                  className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                  data-testid="agent-agency-select"
                >
                  <SelectValue placeholder="Seleccione uma agência" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.filter(a => a.active).map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-uppercase">URL de Reviews (Perfil Social) *</label>
              <Input
                value={formData.review_url}
                onChange={(e) => setFormData({ ...formData, review_url: e.target.value })}
                placeholder="https://facebook.com/... ou https://linkedin.com/..."
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                data-testid="agent-url-input"
              />
            </div>
            <div>
              <label className="label-uppercase">Notas Internas</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas opcionais..."
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none resize-none"
                rows={3}
                data-testid="agent-notes-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="btn-outline"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="btn-gold"
                data-testid="save-agent-btn"
              >
                {submitting ? 'A guardar...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-agent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Consultor</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que deseja eliminar o consultor "{selectedAgent?.name}"?
              Esta acção é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-agent"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>
              Importar Consultores
            </DialogTitle>
            <p className="text-sm text-gray-500">
              Importe consultores automaticamente a partir do Century 21
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="label-uppercase">Agência *</label>
              <select
                value={importAgencyId}
                onChange={(e) => setImportAgencyId(e.target.value)}
                className="w-full h-10 border-2 border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#B5985A]"
                data-testid="import-agency-select"
              >
                <option value="">Seleccione uma agência</option>
                {agencies.filter(a => a.active).map((agency) => (
                  <option key={agency.id} value={agency.id}>{agency.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Serão importados os consultores do Century 21 associados a esta agência
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setImportDialogOpen(false)}
              className="btn-outline"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleImport}
              disabled={importing || !importAgencyId}
              className="btn-gold"
              data-testid="confirm-import-btn"
            >
              {importing ? 'A importar...' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog open={importResultsOpen} onOpenChange={setImportResultsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="import-results-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>
              Resultado da Importação
            </DialogTitle>
            <p className="text-sm text-gray-500">
              {importResults?.total_fetched || 0} consultores encontrados no Century 21
            </p>
          </DialogHeader>
          {importResults && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {importResults.imported_names?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-semibold text-green-700">
                      Importados ({importResults.imported})
                    </span>
                  </div>
                  <ul className="space-y-1 pl-6">
                    {importResults.imported_names.map((name, i) => (
                      <li key={i} className="text-sm text-gray-700">{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {importResults.skipped_names?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-500">
                      Já existentes ({importResults.skipped})
                    </span>
                  </div>
                  <ul className="space-y-1 pl-6">
                    {importResults.skipped_names.map((name, i) => (
                      <li key={i} className="text-sm text-gray-400">{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-xs text-gray-400 mt-2 pt-2 border-t">
                {importResults.gender_ai_used
                  ? `Sexo detectado por IA: ${importResults.gender_ai_detected} consultores${importResults.gender_updated > 0 ? ` (${importResults.gender_updated} existentes actualizados)` : ''}`
                  : 'Sexo por IA indisponível (GEMINI_API_KEY não configurada) — todos definidos como Masculino'}
              </div>
              {importResults.imported === 0 && importResults.skipped === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  Nenhum consultor encontrado para importar.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setImportResultsOpen(false)}
              className="btn-gold"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
